import { Upload01Icon, XIcon } from '@langchain/untitled-ui-icons';

import { filesize } from 'filesize';
import { useRef, useState } from 'react';

import { IconButton, Tooltip } from '@/design-system';
import AlertCircleIcon from '@/icons/AlertCircleIcon.svg?react';
import EarmarkedPageIcon from '@/icons/EarmarkedPageIcon.svg?react';
import { cn } from '@/utils/tailwind';

export const FileUploadSection = (props: {
  fileName: string | null;
  fileSize: number | null;
  error: string | null;
  handleCSVFile?: (file: File) => Promise<void>;
  handleJSONLFile?: (file: File) => void;
  onClear?: () => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDraggedCount, setIsDraggedCount] = useState(0);
  const isDragged = isDraggedCount > 0;

  const handleDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDraggedCount((count) => count - 1);

    if (e.dataTransfer && (props.handleCSVFile || props.handleJSONLFile)) {
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();

        if (fileType === 'text/csv' || fileName.endsWith('.csv')) {
          props.handleCSVFile?.(file);
        } else if (
          fileType === 'application/jsonlines' ||
          fileName.endsWith('.jsonl')
        ) {
          props.handleJSONLFile?.(file);
        }
      }
    }
  };

  const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDraggedCount((count) => count + 1);
  };

  const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
  };

  const handleDragExit: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    setIsDraggedCount((count) => count - 1);
  };

  const uploadComponent = (
    <div
      className={cn(
        'ml-auto mr-auto flex h-full max-h-[1024px] max-w-[1536px] cursor-pointer flex-col items-center justify-center gap-4 self-stretch rounded-xl bg-secondary px-6 hover:bg-tertiary',
        isDragged && 'bg-brand-secondary'
      )}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-[8px] border border-secondary bg-primary">
        <Upload01Icon className="h-5 w-5 text-tertiary" />
      </div>
      <div className="text-center">
        <div className="mb-1 text-sm font-medium">
          <span className="text-brand-tertiary">Click to upload</span>{' '}
          <span className="text-tertiary">or drag and drop</span>
        </div>
        <div className="text-sm text-tertiary">CSV or JSONL</div>
      </div>
    </div>
  );
  const uploadedComponent = (
    <Tooltip title={props.error}>
      <div
        className={cn(
          'flex h-[72px] cursor-pointer items-center justify-between gap-1 self-stretch rounded-xl border border-primary px-6 py-4 hover:bg-secondary',
          isDragged && 'bg-secondary',
          props.error && 'border-error'
        )}
      >
        <div className="flex h-10 flex-1 items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10">
              <div className="absolute left-[7px] top-0">
                <EarmarkedPageIcon />
              </div>

              <div className="absolute left-[1.50px] top-[18px] inline-flex items-start justify-start gap-2 rounded-sm bg-lime-700 px-[3px]">
                <div className="text-center text-[10px] font-bold text-white">
                  {props.fileName?.toLowerCase().endsWith('.csv')
                    ? 'CSV'
                    : 'JSONL'}
                </div>
              </div>
            </div>
            <div className="inline-flex items-center gap-1">
              <div className="mr-1 text-sm font-medium leading-tight">
                {props.fileName}
              </div>
              <div className="text-sm font-normal leading-tight text-slate-500">
                {props.fileSize != null && filesize(props.fileSize)}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {props.error && <AlertCircleIcon className="h-5 w-5 text-error" />}
          {props.onClear && (
            <IconButton
              icon={XIcon}
              onClick={(e) => {
                e.stopPropagation();
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                props.onClear?.();
              }}
              color="secondary"
              variant="plain"
              label="Clear file"
            />
          )}
        </div>
      </div>
    </Tooltip>
  );

  return (
    <div
      className="h-full"
      onClick={() => {
        if (fileInputRef.current) {
          (fileInputRef.current as any).click();
        }
      }}
      onDrop={handleDrop}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragExit}
    >
      <input
        ref={fileInputRef}
        accept={'.csv,.jsonl'}
        type="file"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (
            e.target.files &&
            (props.handleCSVFile || props.handleJSONLFile)
          ) {
            const file = e.target.files[0];
            if (!file?.name) return;
            if (file.name.toLowerCase().endsWith('.csv')) {
              props.handleCSVFile?.(file);
            } else if (file.name.toLowerCase().endsWith('.jsonl')) {
              props.handleJSONLFile?.(file);
            }
          }
        }}
        hidden
      />
      {props.fileName ? uploadedComponent : uploadComponent}
    </div>
  );
};
